<template>
  <div class="wt2-sg wt-text-overlay-field wt2-user-assets-actions">
    <div class="-label">{{ field.label }}</div>
    <div v-if="debug" class="-debug">
      X: {{ value.cropOffsetX }} Y: {{ value.cropOffsetY }} Scale:
      {{ value.cropScale }}
    </div>
    <div class="-main">
      <img v-if="value.url" :src="value.thumbnailUrl" />
      <div v-else class="-thumbnail">
        <span class="-sun wticon wticon-picture-sun"></span>
      </div>
      <div class="-actions-and-icon">
        <div class="-actions">
          <div
            v-if="value.url && value.mediaType === 'VIDEO'"
            class="-action"
            @click="toggleMute"
            v-text="value.volume ? 'Mute' : 'Unmute'"
          />
          <wt-lightbox-dropdown
            class="-action"
            :label="chooseBtnLabelText"
            position="left"
            :filter="filter"
            :show-uploading="false"
            :media-co-assets="mediaCoAssets"
            :show-actions="false"
            :box-offset="90"
            :custom-overlay-mode="true"
            :empty-lightbox-text="emptyLightBoxText"
            :selected="value.id"
            @preview-asset="selectAsset"
          >
            <div class="wt2-text-m2 -title">
              Your Story Media
              <span class="wt2-color-black-l" v-if="!field.allowVideo"
                >(Images Only)</span
              >
            </div>
          </wt-lightbox-dropdown>
          <div v-if="validForCrop" class="-action" @click="cropClicked">
            Crop
          </div>
          <div v-if="value.url" class="-action" @click="removeAsset">
            Remove
          </div>
        </div>
        <div v-if="allowStoryboardingControls" class="-lock-icon">
          <i
            class="wticon wticon-unlocked"
            title="Lock Field for Editing"
            @click="editable = false"
            v-if="editable"
          />
          <i
            class="wticon wticon-locked"
            title="Unlock Field for Editing"
            @click="editable = true"
            v-else
          />
        </div>
      </div>
    </div>
    <template v-if="allowStoryboardingControls">
      <slot></slot>
      <media-link class="-media-link" v-model="link" label="Media"></media-link>
      <div class="-toggle">
        <span>Media Required</span>
        <wt-toggle v-model="mandatory"></wt-toggle>
      </div>
    </template>
  </div>
</template>

<script>
import fieldsMixin from '../mixins/fields.vue';
import wtLightboxDropdown from 'components/assets/wtLightboxDropdown.component.vue';
import MediaLink from 'components/linkedFields/LinkedFieldsMediaLink.vue';
import openCrop from 'components/modals/types/cropping/wtCropping.modal';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { AutoCropHandler } from '@wochitnpm/adjustasset';
import { mapState } from 'vuex';

export default {
  name: 'FieldMedia',
  mixins: [fieldsMixin],

  props: {
    field: {
      type: Object,
      required: true,
    },
    fieldDataModel: {
      type: Object,
      required: false,
    },
    allowStoryboardingControls: {
      type: Boolean,
      default: false,
      required: false,
    },
    stageRect: {
      type: DOMRect,
      required: false,
    },
  },

  components: {
    wtLightboxDropdown,
    MediaLink,
  },

  computed: {
    validForCrop() {
      return (
        this.value.url &&
        this.field.relativeAspect &&
        !(
          this.field?.relativeAspect.bottom === 0 &&
          this.field?.relativeAspect.top === 0 &&
          this.field?.relativeAspect.left === 0 &&
          this.field?.relativeAspect.right === 0
        )
      );
    },
    chooseBtnLabelText() {
      return this.field.allowVideo
        ? this.value.url
          ? 'Replace Media'
          : 'Choose Media'
        : this.value.url
        ? 'Replace Image'
        : 'Choose Image';
    },
    emptyLightBoxText() {
      return app.culture.lightBox.emptyFooterMediaField;
    },
    customOverlays() {
      return this.tmpAGD.customOverlays ?? [];
    },
    allFields() {
      const allFields = [];
      for (const co of this.customOverlays) {
        allFields.push(...co.fields);
      }
      return allFields;
    },
    mediaFields() {
      return this.allFields.filter((f) => f.type === 'MEDIA');
    },
    mediaFieldValues() {
      return this.mediaFields.map((f) => f.value);
    },
    mediaFieldValuesParsed() {
      return this.mediaFieldValues.map(JSON.parse);
    },
    mediaCoAssets() {
      const ids = [];
      for (const f of this.mediaFieldValuesParsed) {
        if (f.id) {
          ids.push(f.id);
        }
      }
      return ids;
    },
    ...mapState('assetEditor', ['tmpAGD', 'workModel']),
  },

  watch: {
    link(newVal) {
      this.$emit('storyboardingControlsChanged', {
        name: 'linkedFields',
        value: [{ type: 'linkedMedia', linkedField: newVal }],
      });
    },
    mandatory(newVal) {
      this.$emit('storyboardingControlsChanged', {
        name: 'mandatory',
        value: newVal,
      });
    },
    editable(newVal) {
      this.$emit('storyboardingControlsChanged', {
        name: 'editable',
        value: newVal,
      });
    },
  },

  data() {
    return {
      debug: false,
      value: {
        url: null,
        thumbnailUrl: null,
        lowresUrl: null,
        expirationDate: null,
        providerId: null,
        providerAssetId: null,
        id: null,
        cropScale: 1,
        cropOffsetX: 0,
        cropOffsetY: 0,
      },
      link: null,
      mandatory: false,
      editable: false,
    };
  },

  created() {
    if (this.field.value) {
      try {
        const o = JSON.parse(this.field.value);
        if (_.isObject(o)) {
          this.value = o;
        }
      } catch (e) {
        console.error('Error parsing FieldMedia value');
      }
    }
    if (this.fieldDataModel?.linkedFields?.length) {
      this.link = this.fieldDataModel?.linkedFields[0].linkedField;
    }
    this.mandatory = this.fieldDataModel?.mandatory || false;
    this.editable = this.fieldDataModel?.editable || false;
    this.emitChange({ newValue: JSON.stringify(this.value), makeDirty: false });
    this.$watch(
      'value',
      (nv) => {
        const newValue = JSON.stringify(nv);
        this.emitChange({ newValue });
      },
      { deep: true }
    );
  },

  methods: {
    cropClicked() {
      const ra = this.field.relativeAspect;

      let offset;
      const left = this.value.cropOffsetX ?? null;
      const top = this.value.cropOffsetY ?? null;
      if (left === null || top === null) {
        offset = null;
      } else {
        offset = { left, top };
      }
      const main = {
        type: 'wallpapermetaasset',
        metadata: {},
        wallpaperMetaData: {
          type: 'SOLID',
          wallpaperData: {
            colorValue: 'rgba(0,0,0,1)',
          },
        },
      };
      openCrop({
        zIndex: 1,
        // main: _.cloneDeep(this.workModel),
        main,
        disableMain: true,
        overlay: {
          metadata: {
            url: this.value.urlCdn,
            height: this.value.height,
            width: this.value.width,
            type:
              this.value.mediaType === 'VIDEO'
                ? 'fragmentmetaasset'
                : 'imagemetaasset',
          },
          agd: {
            offset,
            scale: this.value.cropScale,
          },
          sceneLayout: {
            mainAssetBoundingBox: {
              top: 0,
              bottom: 1,
              left: 0,
              right: 1,
              zIndex: 1,
            },
            overlayAssetBoundingBox: {
              top: ra.top,
              bottom: ra.bottom,
              left: ra.left,
              right: ra.right,
              zIndex: 2,
            },
            switchMainAndOverlay: false,
          },
          displayDuration: 3.022876,
        },
        selectedAsset: 'overlay',
        offset: this.stageRect,
        visibleCurtains: false,
      }).$on('close', (e, { overlay } = {}) => {
        if (!overlay) {
          return;
        }
        this.value.cropOffsetX = overlay.offset?.left ?? null;
        this.value.cropOffsetY = overlay.offset?.top ?? null;
        this.value.cropScale = overlay.scale ?? null;
        this.fixValues();
      });
    },
    toggleMute() {
      const isMuted = !this.value.volume || this.value.volume === 0;
      this.value.volume = isMuted ? 1 : 0;
    },
    fixValues(x, y, scale) {
      if (_.isNumber(x)) {
        x = app.sfp(x);
      }
      if (_.isNumber(y)) {
        y = app.sfp(y);
      }
      if (_.isNumber(scale)) {
        scale = app.sfp(scale);
      }
      return [x, y, scale];
    },
    filter(asset) {
      const videoCheck = this.field.allowVideo
        ? true
        : !app.assetType.isFRAGMENT(asset) &&
          !app.assetType.isSOCIALFRAGMENT(asset);
      return (
        videoCheck &&
        !app.assetType.isGIF(asset) &&
        !app.assetType.isWEBPAGE(asset) &&
        !app.assetType.isMAP(asset) &&
        !(
          app.assetType.isSOCIAL(asset) && !app.assetType.isSOCIALIMAGE(asset)
        ) &&
        !app.assetType.isWALLPAPER(asset)
      );
    },
    removeAsset() {
      const oldValue = _.cloneDeep(this.value);
      const newValue = {
        thumbnailUrl: null,
        lowresUrl: null,
        expirationDate: null,
        id: null,
        url: null,
        urlCdn: null,
      };
      bus.$emit('selectedMediaCo', {
        oldValue,
        newValue,
        name: this.field.name,
      });
      this.value = newValue;
      this.link = null;
    },
    selectAsset(asset) {
      const meta = asset.metadata;
      const isVideo = asset.type === app.assetType.FRAGMENT;
      this.meta = meta;
      const value = {};
      value.thumbnailUrl =
        meta.thumbnail160x90Url ||
        meta.thumbnail256x144Url ||
        meta.urlCdn ||
        meta.providerLowResAssetUrl;
      value.lowresUrl = meta.url || meta.urlCdn;
      value.expirationDate = meta.expirationDate;
      value.urlCdn = meta.urlCdn || meta.providerLowResAssetUrl;
      value.height = meta.height;
      value.width = meta.width;
      value.mediaType = isVideo ? 'VIDEO' : 'IMAGE';
      if (isVideo) {
        value.segmentStartTime = asset.startTime;
        value.segmentEndTime = asset.endTime;
        value.duration = meta.duration;
      }
      if (meta.attribution) {
        value.attribution = meta.attribution;
        value.attributionMode = meta.attributionMode;
      }
      value.providerId = meta.providerId;
      value.providerAssetId = meta.providerAssetId;
      value.id = meta.assetUUID;
      value.url = meta.providerDownloadedAssetUrl || meta.hiResAssetUrl;
      value.volume = this.value?.volume || 0;
      if (this.field.relativeAspect) {
        const [x, y, scale] = this.autoCrop(value);
        value.cropOffsetX = x;
        value.cropOffsetY = y;
        value.cropScale = scale;
      } else {
        value.cropOffsetX = null;
        value.cropOffsetY = null;
        value.cropScale = 1;
      }
      bus.$emit('selectedMediaCo', {
        oldValue: this.value,
        newValue: value,
        name: this.field.name,
      });
      this.value = value;
    },
    autoCrop({ width, height }) {
      const ra = this.field.relativeAspect;
      const { scale: autoCropScale, offset: autoCropOffset } =
        new AutoCropHandler().AdjustAsset({
          originalRatio: {
            height: height,
            width: width,
          },
          innerRatio: {
            height: app.ASSET_EDITOR_HEIGHT * (ra.bottom - ra.top),
            width: app.ASSET_EDITOR_WIDTH * (ra.right - ra.left),
          },
          baseRatio: {
            height: app.ASSET_EDITOR_HEIGHT,
            width: app.ASSET_EDITOR_WIDTH,
          },
        });
      const x = autoCropOffset?.left ?? null;
      const y = autoCropOffset?.top ?? null;
      const autoScale = autoCropScale || null;
      return this.fixValues(x, y, autoScale);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';

/deep/ .-lightbox-wrapper .-toggle {
  height: 20px;
}

/deep/ .-lightbox-wrapper .-toggle > div {
  color: $color-primary;
}

/deep/ .-lightbox-wrapper .-toggle > div:hover {
  color: $color-blue-d;
}

/deep/ .-lightbox-wrapper .-toggle:hover {
  background-color: transparent;
}
/deep/ .-lightbox-wrapper .-toggle.-opened {
  background-color: transparent;
}

/deep/ .wt-lightbox-asset .-action.-remove .wticon {
  right: unset;
}

.-media-link {
  margin-top: 15px;
}

.wt-custom-overlay-field {
  .-actions-and-icon {
    .-lock-icon {
      display: flex;
      width: 20px;
      justify-content: center;
      align-items: center;

      i.wticon {
        position: unset;
        transform: unset;
        font-size: 16px;
      }
    }
  }

  .wticon-locked {
    transform: translateX(-3px);
  }

  .wticon-unlocked {
    transform: unset;
  }
}

.-actions-and-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.-actions {
  height: 20px;
  display: flex;
  flex: 1;
  align-items: center;

  .-action {
    padding: 0 8px;
    color: $color-primary;
    width: auto;
    border-right: rgba(0, 0, 0, 0.2) 1px solid;
    &:hover {
      color: $color-blue-d;
      cursor: pointer;
    }
    &:last-child {
      border-right: none;
    }
  }
}

.-main {
  display: flex;
  align-items: center;
}
.-label {
  margin-bottom: 10px;
}
img {
  width: 68px;
  height: 68px;
  border: $color-black-l1 1px solid;
  object-fit: cover;
  object-position: center;
}
.-thumbnail {
  width: 68px;
  height: 68px;
  background-color: #4a4a4a;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wt-custom-overlay-field .-sun {
  position: unset;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.3);
  transform: unset;
  cursor: unset;
}

.-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}
</style>
